// Copy for the Strava connection states both clients show (web Connected
// sources, login and the AppShell banner; mobile integrations, login, signup
// and the Home banner). One place so the wording stays the same everywhere.
// The API keeps its own copy of STRAVA_RECONNECT_COPY in
// packages/api/src/lib/sync-run-state.ts (it has no @atlas/shared dependency).

/** The callback refused a grant without the activity permission (?error=strava_scope). */
export const STRAVA_SCOPE_ERROR_COPY =
  "Atlas needs to see your activities. On Strava's screen, leave both activity boxes ticked, then try again.";

/** The connection was refused after the fact (IntegrationRow.needsReconnect). */
export const STRAVA_RECONNECT_COPY = "Strava stopped sharing your activities with Atlas. Reconnect to carry on.";

/** Banner title and line for the same state, outside Connected sources. */
export const STRAVA_RECONNECT_BANNER = {
  title: "Strava stopped sharing your activities",
  detail: "Reconnect from Connected sources and leave both activity boxes ticked. Nothing on your record is lost.",
  action: "Reconnect",
} as const;

/** Connected with activity:read only (?scope=public on the success redirect). */
export const STRAVA_PUBLIC_ONLY_COPY =
  "Atlas can only see activities you've made public on Strava. To bring in private ones too, connect again and leave both activity boxes ticked.";

/** "Last sync failed. <reason>": the API sends a plain reason, this is the fallback. */
export function stravaSyncFailedLine(error: string | null | undefined): string {
  return `Last sync failed. ${error?.trim() || "Try Sync now."}`;
}

/**
 * Empty state for a Strava connection whose last full sync matched nothing
 * (ATL-173). `skipped` is how many activities Strava listed that Atlas did
 * not bring in; 0 / 0 means Strava listed nothing at all, which is usually
 * private activities on a public-only grant. `historyTruncated` comes from
 * GET /billing (the free plan's 12-month window actually held walks back).
 */
export function stravaEmptySyncCopy(opts: {
  skipped: number;
  historyTruncated: boolean;
}): string {
  if (opts.skipped === 0) {
    return "Strava didn't list any activities for Atlas. If your walks are set to Only you, Atlas needs permission to see private activities: connect again and leave both activity boxes ticked.";
  }
  const listed = `Strava listed ${opts.skipped.toLocaleString("en-GB")} ${opts.skipped === 1 ? "activity" : "activities"}, but none Atlas could match.`;
  const reasons = opts.historyTruncated
    ? "Atlas matches walks, runs and hikes, and the free plan matches the last 12 months."
    : "Atlas matches walks, runs and hikes only, so rides and gym sessions stay out.";
  return `${listed} ${reasons} Private activities need both activity boxes ticked when you connect.`;
}
